import type { AlfredGlobalConfig, ProVenueInstance } from "@/types/alfred-config"
import type { AlfredDeviceConfig, AlfredDevicesResponse } from "@/types/alfred-device"

// The UI must never consume, retain, or display these write-only credential
// values, even if an older backend still returns them in a GET response:
//
//   - ticketmasterConsumerKey
//   - proVenueInstances[].consumerKey
//   - proVenueInstances[].consumerSecret
//
// These helpers strip the fields at the API normalization boundary so the
// secrets never reach UI state. They accept the current backend shape (fields
// already absent) as well as the legacy shape (fields present) so the UI stays
// compatible with both response formats.

// Reduce each ProVenue instance to its non-sensitive metadata, discarding any
// consumerKey/consumerSecret an older backend may still include.
export function normalizeProVenueInstances(value: unknown): ProVenueInstance[] {
  if (!Array.isArray(value)) return []
  return value.map((entry) => {
    const record = (entry ?? {}) as Record<string, unknown>
    return {
      name: typeof record.name === "string" ? record.name : "",
      hostName: typeof record.hostName === "string" ? record.hostName : "",
    }
  })
}

export function normalizeAlfredDevice(device: AlfredDeviceConfig): AlfredDeviceConfig {
  const { ticketmasterConsumerKey: _ticketmasterConsumerKey, ...rest } =
    device as AlfredDeviceConfig & { ticketmasterConsumerKey?: unknown }
  return {
    ...rest,
    proVenueInstances: normalizeProVenueInstances(device.proVenueInstances),
  }
}

export function normalizeAlfredDevicesResponse(
  response: AlfredDevicesResponse,
): AlfredDevicesResponse {
  return {
    ...response,
    items: Array.isArray(response.items) ? response.items.map(normalizeAlfredDevice) : [],
  }
}

export function normalizeAlfredGlobalConfig(config: AlfredGlobalConfig): AlfredGlobalConfig {
  const { ticketmasterConsumerKey: _ticketmasterConsumerKey, ...rest } =
    config as AlfredGlobalConfig & { ticketmasterConsumerKey?: unknown }
  return {
    ...rest,
    proVenueInstances: normalizeProVenueInstances(config.proVenueInstances),
  }
}
